/**
 * Single source of truth for the Russian (UI) labels of conversation statuses.
 *
 * IMPORTANT: only the *display* string is localized — the machine status values
 * (`open` | `pending` | `waiting` | `resolved` | `snoozed` | `closed`) are never
 * translated. Use {@link statusLabelRu} when you have a status that might fall
 * outside the known set (e.g. a widened union) so unknown values degrade
 * gracefully instead of rendering `undefined`.
 */
export const STATUS_LABEL_RU = {
    open: 'Открыта',
    pending: 'В ожидании',
    waiting: 'Ждём ответа',
    snoozed: 'Отложена',
    resolved: 'Решена',
    closed: 'Закрыта',
};
/**
 * Safe lookup of a status' RU label. Falls back to the raw status string for
 * any value not in {@link STATUS_LABEL_RU} (defensive against a widened enum).
 */
export function statusLabelRu(status) {
    return STATUS_LABEL_RU[status] ?? status;
}
/**
 * Statuses a UI may treat as «дело закрыто» when it needs to fold finished
 * conversations away. Deliberately the SAME pair the Telegram card's state
 * machine calls terminal (`packages/telegram-notifier/src/state-machine.ts:26`),
 * so «закрытые» means one thing across surfaces; kept here rather than imported
 * because that package is server-side and admin-web does not depend on it.
 *
 * NOT a substitute for a server-side filter — the inbox's status filter still
 * sends explicit status lists.
 */
export const TERMINAL_CONVERSATION_STATUSES = ['resolved', 'closed'];
/** Whether `status` is one of {@link TERMINAL_CONVERSATION_STATUSES}. */
export function isTerminalConversationStatus(status) {
    return TERMINAL_CONVERSATION_STATUSES.includes(status);
}
/**
 * RU-подписи статуса запуска FB (`displayStatus` из auto-ads, спека
 * fb-launch-builder-v2 §8). Машинные значения не переводятся; неизвестное
 * значение (контракт расширили раньше UI) показывается как есть через
 * {@link adsLaunchStatusLabelRu}.
 */
export const ADS_LAUNCH_DISPLAY_STATUS_LABEL_RU = {
    draft: 'Черновик',
    scheduled: 'Ждёт создания',
    creating: 'Создаётся',
    ready_paused: 'Готов, на паузе',
    blocked: 'Расхождения — не включён',
    activating: 'Включается',
    waiting_start: 'Ждёт старта',
    running: 'Активен',
    cancelled: 'Отменён',
    paused: 'На паузе',
    paused_on_drift: 'Поставлен на паузу: подмена',
    pause_failed: 'Пауза не удалась — требует внимания',
    needs_attention: 'Требует внимания',
    failed: 'Ошибка',
    rolling_back: 'Откат',
    rolled_back: 'Удалён',
};
/**
 * «Требует внимания» уточняется причиной (`activation.reason` auto-ads):
 * подмена после включения или сверка, которую не удалось провести.
 */
export const ADS_NEEDS_ATTENTION_REASON_LABEL_RU = {
    drift: 'Подмена после включения',
    'verify-unavailable': 'Не удалось проверить — требует внимания',
};
/**
 * Запуск создан, но не включён — почему (`activation.reason`): старт слишком
 * близко к моменту создания, опоздал к старту, пачку отменили.
 */
export const ADS_READY_PAUSED_REASON_LABEL_RU = {
    'start-too-close': 'На паузе: старт слишком близко',
    late: 'На паузе: опоздал к старту',
    cancelled: 'На паузе: пачка отменена',
};
/**
 * Сверка сразу после создания нашла подмену (создание «как в Ads Manager»):
 * запуск поставлен на паузу до первого показа.
 */
export const ADS_BLOCKED_REASON_LABEL_RU = {
    drift: 'Подмена при создании — на паузе',
};
const ADS_STATUS_REASON_LABEL_RU = {
    needs_attention: ADS_NEEDS_ATTENTION_REASON_LABEL_RU,
    ready_paused: ADS_READY_PAUSED_REASON_LABEL_RU,
    blocked: ADS_BLOCKED_REASON_LABEL_RU,
};
/**
 * Подпись статуса запуска; для `needs_attention`, `ready_paused` и `blocked`
 * с известной причиной — подпись причины. Незнакомое — как есть.
 */
export function adsLaunchStatusLabelRu(status, reason) {
    if (reason != null) {
        const byReason = Object.hasOwn(ADS_STATUS_REASON_LABEL_RU, status)
            ? ADS_STATUS_REASON_LABEL_RU[status][reason]
            : undefined;
        if (byReason)
            return byReason;
    }
    return ADS_LAUNCH_DISPLAY_STATUS_LABEL_RU[status] ?? status;
}
/** RU-подписи состояния пачки запусков (вычисляется auto-ads из её запусков). */
export const ADS_BATCH_STATE_LABEL_RU = {
    draft: 'Черновик',
    running: 'Выполняется',
    needs_attention: 'Требует внимания',
    failed: 'Ошибка',
    active: 'Запущена',
    done_manual: 'Создана на паузе',
    cancelled: 'Отменена',
    rolled_back: 'Удалена',
};
export function adsBatchStateLabelRu(state) {
    return ADS_BATCH_STATE_LABEL_RU[state] ?? state;
}
/** RU-подписи состояния файла медиатеки в кабинете (спека fb-launch-builder-v2 §1.4). */
export const ADS_MEDIA_STATE_LABEL_RU = {
    queued: 'Получен',
    uploading: 'Загружается в Meta',
    processing: 'Обрабатывается Meta',
    ready: 'Готов',
    failed: 'Ошибка',
    archived: 'В архиве',
};
export function adsMediaStateLabelRu(state) {
    return ADS_MEDIA_STATE_LABEL_RU[state] ?? state;
}
//# sourceMappingURL=status-labels.js.map